import { apiClient } from "@/lib/apiClient";
import { Product, Variant } from "@/types/models/product";
import {
  CrudService,
  CrudFilters,
  FetchOptions,
  SearchOptions,
  CreateRequest,
  BulkUpdateRequest,
  BulkDeleteRequest,
} from "@/types/crud";

// Product-specific filter types
export interface ProductFilters extends CrudFilters {
  status?: string;
  tags?: string[];
  price_min?: number;
  price_max?: number;
  inventory_min?: number;
  inventory_max?: number;
  title?: string;
  collection_id?: string;
}

export interface ProductCreateRequest {
  title: string;
  description?: string;
  status: "ACTIVE" | "DRAFT" | "INACTIVE";
  vendor?: string;
  product_type?: string;
  tags?: string[];
  images?: string[];
  variants: Omit<Variant, "id" | "product_id">[];
  options?:
    | Array<{
        name: string;
        values: string[];
      }>
    | undefined;
  seo_title?: string;
  seo_description?: string;
}

export interface ProductUpdateRequest extends Partial<ProductCreateRequest> {
  id: string;
}

// Backwards-compatible exported types expected by other modules
export type FetchProductsOptions = import("@/types/crud").FetchOptions<any>;
export type SearchProductsOptions = import("@/types/crud").SearchOptions<any>;
export type CreateProductRequest = ProductCreateRequest;
export type UpdateProductRequest = ProductUpdateRequest;

export interface BulkUpdateInventoryRequest {
  updates: Array<{
    variant_id: string;
    inventory: number;
  }>;
}

// Product CRUD Service Implementation
class ProductServiceImpl implements CrudService<Product, ProductFilters> {
  async getAll(options: FetchOptions<ProductFilters> = {}) {
    const params: Record<string, string> = {};

    if (options.page) params.page = options.page.toString();
    if (options.pageSize) params.pageSize = options.pageSize.toString();
    if (options.sortBy) params.sortBy = options.sortBy;
    if (options.sortOrder) params.sortOrder = options.sortOrder;

    // Add filters
    if (options.filters) {
      if (options.filters.status) params.status = options.filters.status;
      if (options.filters.title) params.title = options.filters.title;
      if (options.filters.price_min)
        params.price_min = options.filters.price_min.toString();
      if (options.filters.price_max)
        params.price_max = options.filters.price_max.toString();
      if (options.filters.inventory_min)
        params.inventory_min = options.filters.inventory_min.toString();
      if (options.filters.inventory_max)
        params.inventory_max = options.filters.inventory_max.toString();
      if (options.filters.collection_id)
        params.collection_id = options.filters.collection_id;
      if (options.filters.tags && options.filters.tags.length > 0) {
        params.tags = options.filters.tags.join(",");
      }
    }

    return await apiClient.getPaginated(
      "/api/v1/admin/:storeId/products",
      params,
    );
  }

  async getById(productId: string) {
    return await apiClient.get(`/api/v1/admin/:storeId/products/${productId}`);
  }

  async create(product: CreateRequest<Product>) {
    return await apiClient.post("/api/v1/admin/:storeId/products", product);
  }

  async update(productId: string, product: Partial<Product>) {
    return await apiClient.put(
      `/api/v1/admin/:storeId/products/${productId}`,
      product,
    );
  }

  async delete(productId: string) {
    return await apiClient.delete(
      `/api/v1/admin/:storeId/products/${productId}`,
    );
  }

  async search(options: SearchOptions<ProductFilters>) {
    const params: Record<string, string> = {
      q: options.query,
    };

    if (options.page) params.page = options.page.toString();
    if (options.pageSize) params.pageSize = options.pageSize.toString();
    if (options.sortBy) params.sortBy = options.sortBy;
    if (options.sortOrder) params.sortOrder = options.sortOrder;

    // Add filters
    if (options.filters) {
      if (options.filters.status) params.status = options.filters.status;
      if (options.filters.price_min)
        params.price_min = options.filters.price_min.toString();
      if (options.filters.price_max)
        params.price_max = options.filters.price_max.toString();
      if (options.filters.inventory_min)
        params.inventory_min = options.filters.inventory_min.toString();
      if (options.filters.inventory_max)
        params.inventory_max = options.filters.inventory_max.toString();
      if (options.filters.collection_id)
        params.collection_id = options.filters.collection_id;
      if (options.filters.tags && options.filters.tags.length > 0) {
        params.tags = options.filters.tags.join(",");
      }
    }

    return await apiClient.getPaginated(
      "/api/v1/admin/:storeId/products/search",
      params,
    );
  }

  async bulkUpdate(data: BulkUpdateRequest) {
    return await apiClient.put("/api/v1/admin/:storeId/products/bulk", data);
  }

  async bulkDelete(data: BulkDeleteRequest) {
    return await apiClient.delete("/api/v1/admin/:storeId/products/bulk", {
      ids: data.ids,
    });
  }
}

/** Bumps catalog/collection cache versions and clears variant price Redis keys for the current store. */
export const invalidateCatalogCache = async () => {
  return await apiClient.post(
    "/api/v1/admin/:storeId/products/cache/invalidate-catalog",
  );
};

// Export service instance
export const productService = new ProductServiceImpl();

// Legacy exports for backwards compatibility
export const getProducts = productService.getAll.bind(productService);
export const getProductById = productService.getById.bind(productService);
export const createProduct = productService.create.bind(productService);
export const updateProduct = productService.update.bind(productService);
export const deleteProduct = productService.delete.bind(productService);
export const searchProducts = productService.search.bind(productService);

export const bulkFetchProducts = async (productIds: string[]) => {
  return await apiClient.post("/api/v1/admin/:storeId/products/bulk", {
    ids: productIds,
  });
};

// Product Variants
export const getProductVariant = async (
  productId: string,
  variantId: string,
) => {
  return await apiClient.get(
    `/api/v1/admin/:storeId/products/${productId}/variants/${variantId}`,
  );
};

export const bulkFetchVariants = async (variantIds: string[]) => {
  return await apiClient.post("/api/v1/admin/:storeId/products/variants/bulk", {
    ids: variantIds,
  });
};

export const searchProductVariants = async (
  options: SearchOptions<ProductFilters>,
) => {
  const params: Record<string, string> = {
    q: options.query,
  };

  if (options.page) params.page = options.page.toString();
  if (options.pageSize) params.pageSize = options.pageSize.toString();

  if (options.filters) {
    if (options.filters.price_min)
      params.price_min = options.filters.price_min.toString();
    if (options.filters.price_max)
      params.price_max = options.filters.price_max.toString();
    if (options.filters.inventory_min)
      params.inventory_min = options.filters.inventory_min.toString();
    if (options.filters.inventory_max)
      params.inventory_max = options.filters.inventory_max.toString();
  }

  return await apiClient.getPaginated(
    "/api/v1/admin/:storeId/products/variants/search",
    params,
  );
};

// Inventory Management
export const getInventory = async (
  options: FetchOptions<ProductFilters> = {},
) => {
  const params: Record<string, string> = {};

  if (options.page) params.page = options.page.toString();
  if (options.pageSize) params.pageSize = options.pageSize.toString();

  return await apiClient.getPaginated(
    "/api/v1/admin/:storeId/products/inventory",
    params,
  );
};

export const updateInventory = async (updates: BulkUpdateInventoryRequest) => {
  return await apiClient.put(
    "/api/v1/admin/:storeId/products/inventory",
    updates,
  );
};

// Product Reviews
export const getProductReviews = async (
  productId: string,
  page = 1,
  pageSize = 10,
) => {
  const params = {
    page: page.toString(),
    pageSize: pageSize.toString(),
  };

  return await apiClient.getPaginated(
    `/api/v1/admin/:storeId/products/${productId}/reviews`,
    params,
  );
};

export const createProductReview = async (
  productId: string,
  review: {
    rating: number;
    title?: string;
    content?: string;
    customer_name?: string;
    customer_email?: string;
  },
) => {
  return await apiClient.post(
    `/api/v1/admin/:storeId/products/${productId}/reviews`,
    review,
  );
};

export const deleteProductReview = async (
  productId: string,
  reviewId: string,
) => {
  return await apiClient.delete(
    `/api/v1/admin/:storeId/products/${productId}/reviews/${reviewId}`,
  );
};

export const approveProductReview = async (
  productId: string,
  reviewId: string,
) => {
  return await apiClient.put(
    `/api/v1/admin/:storeId/products/${productId}/reviews/${reviewId}/approve`,
  );
};

// Import/Export
export const importProductsFromExcel = async (
  file: File,
  updateExisting = false,
) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("updateExisting", updateExisting.toString());

  return await apiClient.uploadFile(
    "/api/v1/admin/:storeId/products/import/excel",
    file,
    updateExisting !== undefined
      ? { updateExisting: updateExisting.toString() }
      : undefined,
  );
};

export const importDummyProducts = async () => {
  return await apiClient.post("/api/v1/admin/:storeId/products/import/dummy");
};

export const exportProductsToExcel = async (options?: {
  format?: "flat" | "nested";
  includeVariants?: boolean;
  includeImages?: boolean;
}) => {
  const params: Record<string, string> = {};

  if (options?.format) params.format = options.format;
  if (options?.includeVariants !== undefined)
    params.includeVariants = options.includeVariants.toString();
  if (options?.includeImages !== undefined)
    params.includeImages = options.includeImages.toString();

  return await apiClient.get(
    "/api/v1/admin/:storeId/products/export/excel-complex",
    params,
  );
};

// Asset Management
export const uploadProductAsset = async (file: File, filename: string) => {
  return await apiClient.uploadFile(
    `/api/v1/admin/:storeId/products/assets/${filename}`,
    file,
  );
};
